import Icon from '@atoms/Icon/Icon';
import { IconName } from '@models/IIcon';
import IconSizeEnum from '@models/enums/IconSizeEnum';
import updateDataLayer from '@utils/helpers/gtm';
import useAriaLabels from '@utils/hooks/useAriaLabels';
import useResizeObserver from '@utils/hooks/useResizeObserver';
import {
  ChangeEvent,
  Dispatch,
  FormEvent,
  SetStateAction,
  useCallback,
} from 'react';

interface Props {
  readonly listingPage: string;
  readonly overlaySearchIcon: IconName;
  readonly searchPlaceholder: string;
  readonly searchSubTitle: string;
  readonly setValue: Dispatch<SetStateAction<string>>;
  readonly value: string;
  readonly onTypingDone?: () => void;
}

const SearchBar = ({
  listingPage,
  overlaySearchIcon,
  searchPlaceholder,
  searchSubTitle,
  setValue,
  value,
  onTypingDone,
}: Props) => {
  const [defaultUnderlineRef, { width: maxUnderlineWidth }] =
    useResizeObserver();
  const [hiddenSearchRef, { width: currentTextUnderlineWidth }] =
    useResizeObserver();
  const { searchAriaLabel } = useAriaLabels();
  let typingTimer: NodeJS.Timeout;

  const searchFieldRef = useCallback((node: HTMLElement | null) => {
    node?.focus();
  }, []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();

    updateDataLayer({
      event: 'searchBar',
      search_term: value,
    });

    const convertedValue = value
      .replace(/\+/g, '%2B') // allows + symbol to be used in search input
      .replace(/%(?![0-9A-Fa-f]{2})/g, '%25'); // allows standalone % symbol to be used in search input
    window.location.href = value
      ? `${listingPage}?query=${convertedValue}`
      : `${listingPage}`;
  };

  const onChange = (event: ChangeEvent<HTMLInputElement>) =>
    setValue(event.target.value);

  const onKeyDown = () => clearTimeout(typingTimer);

  const onKeyUp = () => {
    const doneTypingInterval = 500;
    clearTimeout(typingTimer);
    typingTimer = setTimeout(() => {
      onTypingDone?.();
    }, doneTypingInterval);
  };

  return (
    <div className="grid grid-cols-12">
      <div className="col-span-full md:col-span-10 lg:col-span-8">
        <p
          className="body-one bold mb-0.5 md:mb-7.5"
          data-testid="CT-48-SearchBar-Subtitle"
        >
          {searchSubTitle}
        </p>

        <form onSubmit={onSubmit} className="relative">
          <div
            className="flex items-center gap-x-4"
            data-testid="CT-48-SearchBar-Input-Container"
          >
            <input
              ref={searchFieldRef}
              type="text"
              className="h2 flex h-full w-full appearance-none overflow-auto bg-transparent placeholder-grey-500 outline-none outline-0"
              placeholder={searchPlaceholder}
              value={value}
              onChange={onChange}
              {...(onTypingDone
                ? {
                    onKeyUp,
                    onKeyDown,
                    onFocus: onKeyUp,
                  }
                : {})}
              data-testid="CT-48-SearchBar-Input"
            />

            <button
              type="submit"
              className="z-80 h-11 w-11 shrink-0 rounded-full bg-yellow md:h-13 md:w-13"
              aria-label={searchAriaLabel}
              data-testid="CT-48-SearchBar-Submit-Button"
            >
              <Icon name={overlaySearchIcon} size={IconSizeEnum.xl} />
            </button>
          </div>

          <div
            ref={defaultUnderlineRef}
            className="absolute left-0 z-80 mt-2.5 h-0.5 w-full bg-grey-400 md:mt-7.5"
          />
          <div
            style={{
              width: Math.min(maxUnderlineWidth, currentTextUnderlineWidth),
            }}
            className="absolute left-0 z-80 mt-2.5 h-0.5 bg-black-100 md:mt-7.5"
          />
          <span
            ref={hiddenSearchRef}
            aria-hidden="true"
            className="h2 invisible absolute inline-block overflow-hidden whitespace-pre text-transparent"
            style={{ maxWidth: maxUnderlineWidth }}
            data-testid="CT-48-SearchBar-Search-Value"
          >
            {value}
          </span>
        </form>
      </div>
    </div>
  );
};

export default SearchBar;
